import { useEffect, useState } from "react";
import { API, rupiah, errMsg, CATEGORIES, catLabel } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Copy, TicketPercent } from "lucide-react";

const EMPTY = { category: "semua", discount: "", valid_from: "", valid_until: "", requirements: "", min_purchase: 0, is_active: true, is_multiply: false, discount_type: "nominal" };

export default function Vouchers() {
  const [vouchers, setVouchers] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = () => API.get("/vouchers").then((r) => setVouchers(r.data)).catch(() => setVouchers([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, discount: Number(form.discount) || 0, min_purchase: Number(form.min_purchase) || 0 };
      if (editId) {
        await API.put(`/vouchers/${editId}`, payload);
        toast.success("Voucher diperbarui");
      } else {
        const { data } = await API.post("/vouchers", payload);
        toast.success(`Voucher dibuat — kode unik: ${data.code}`);
      }
      setForm(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus voucher ini?")) return;
    await API.delete(`/vouchers/${id}`);
    toast.success("Voucher dihapus");
    load();
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Kode ${code} disalin`);
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";

  return (
    <div data-testid="admin-vouchers">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Voucher Diskon</h1>
          <p className="text-sm text-slate-500">Kode unik acak otomatis — bagikan ke pelanggan, dipakai di keranjang belanja</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY }); setEditId(null); }} data-testid="add-voucher" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
          <Plus className="h-4 w-4" /> Buat Voucher
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="vouchers-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Kode Unik</th>
              <th className="px-5 py-3">Kategori</th>
              <th className="px-5 py-3">Diskon</th>
              <th className="px-5 py-3">Min. Pembelian</th>
              <th className="px-5 py-3">Berlaku</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(vouchers || []).map((v) => (
              <tr key={v.id} data-testid={`voucher-row-${v.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3">
                  <button onClick={() => copy(v.code)} data-testid={`voucher-code-${v.id}`} className="inline-flex items-center gap-2 font-mono-accent font-bold text-[#0B1F3A] hover:text-[#C9A227] transition-colors" title="Salin kode">
                    {v.code} <Copy className="h-3.5 w-3.5" />
                  </button>
                </td>
                <td className="px-5 py-3">{v.category === "semua" ? "Semua Produk" : catLabel(v.category)}</td>
                <td className="px-5 py-3 font-bold text-emerald-700">
                  {v.discount_type === "persen" ? `${v.discount}%` : rupiah(v.discount)}
                  {v.is_multiply && (!v.discount_type || v.discount_type === "nominal") && <span className="block text-[10px] text-amber-600 font-bold" data-testid={`voucher-multiply-badge-${v.id}`}>× jumlah item terkait</span>}
                  {v.discount_type === "kelipatan_min" && <span className="block text-[10px] text-amber-600 font-bold">× kelipatan min. pembelian</span>}
                  {v.discount_type === "persen" && <span className="block text-[10px] text-amber-600 font-bold">dari subtotal belanja</span>}
                </td>
                <td className="px-5 py-3">{v.min_purchase > 0 ? rupiah(v.min_purchase) : "-"}</td>
                <td className="px-5 py-3 text-xs text-slate-500">
                  {v.valid_from || "..."} s.d. {v.valid_until || "..."}
                  {v.requirements && <span className="block text-slate-400">Syarat: {v.requirements}</span>}
                </td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${v.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                    {v.is_active ? "AKTIF" : "NONAKTIF"}
                  </span>
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <button onClick={() => { setForm({ category: v.category, discount: v.discount, valid_from: v.valid_from || "", valid_until: v.valid_until || "", requirements: v.requirements || "", min_purchase: v.min_purchase || 0, is_active: v.is_active, is_multiply: v.is_multiply || false, discount_type: v.discount_type || "nominal" }); setEditId(v.id); }} data-testid={`edit-voucher-${v.id}`} className="p-1.5 text-slate-400 hover:text-[#0B1F3A]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(v.id)} data-testid={`delete-voucher-${v.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {vouchers && vouchers.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400"><TicketPercent className="h-8 w-8 mx-auto mb-2 text-slate-300" />Belum ada voucher.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-md max-h-[90vh] overflow-y-auto p-6" data-testid="voucher-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Voucher" : "Buat Voucher Diskon"}</h2>
              <button onClick={() => setForm(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              {!editId && <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">Kode unik voucher dibuat otomatis secara acak setelah Anda menyimpan.</p>}
              <div>
                <label className={labelCls}>Kategori Produk</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="voucher-category" className={inputCls}>
                  <option value="semua">Semua Produk</option>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Tipe Diskon</label>
                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="voucher-type" className={inputCls}>
                  <option value="nominal">Nominal tetap (Rp)</option>
                  <option value="kelipatan_min">Berkelipatan dari minimum pembelian (Rp × kelipatan tercapai)</option>
                  <option value="persen">Persentase dari subtotal belanja (%)</option>
                </select>
                {form.discount_type === "kelipatan_min" && (
                  <p className="text-xs text-slate-400 mt-1.5">Contoh: diskon Rp 10.000, min. pembelian Rp 100.000 → belanja Rp 350.000 dapat 3× = Rp 30.000. Wajib isi Minimum Pembelian di bawah.</p>
                )}
                {form.discount_type === "persen" && (
                  <p className="text-xs text-slate-400 mt-1.5">Contoh: diskon 10% → belanja Rp 500.000 mendapat potongan Rp 50.000.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Besaran Diskon ({form.discount_type === "persen" ? "%" : "Rp"}) *</label>
                <input type="number" min="0" max={form.discount_type === "persen" ? 100 : undefined} value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="voucher-discount" className={inputCls} placeholder={form.discount_type === "persen" ? "Contoh: 10" : "Contoh: 25000"} />
              </div>
              {(!form.discount_type || form.discount_type === "nominal") && (
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_multiply} onChange={(e) => setForm({ ...form, is_multiply: e.target.checked })} data-testid="voucher-multiply" className="h-4 w-4 mt-0.5 accent-[#0B1F3A]" />
                  <span>
                    Diskon berlipat — dikali jumlah item terkait yang dibeli
                    <span className="block text-xs text-slate-400">Contoh: diskon Rp 10.000, pelanggan membeli 5 item kategori terkait → potongan Rp 50.000</span>
                  </span>
                </label>
              )}
              <div>
                <label className={labelCls}>Minimum Pembelian (Rp)</label>
                <input type="number" min="0" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })} data-testid="voucher-min" className={inputCls} placeholder="0 = tanpa minimum" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Berlaku Dari</label>
                  <input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="voucher-from" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Berlaku Sampai</label>
                  <input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="voucher-until" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Persyaratan Berlaku</label>
                <input value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} data-testid="voucher-requirements" className={inputCls} placeholder="Mis: khusus pelanggan setia / 1x pakai" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="voucher-active" className="h-4 w-4 accent-[#0B1F3A]" />
                Voucher aktif
              </label>
              <button onClick={save} disabled={!form.discount} data-testid="save-voucher" className="w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
                {editId ? "Simpan Perubahan" : "Buat Voucher & Generate Kode"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
